import { useParams } from 'react-router-dom'
import { TableComponentProps } from '../../../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { InputTableURLsTable } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { useCallback, useEffect, useState } from 'react'
import SingleWorkSheetInner from './SingleWorkSheetInner'
import WorkSheetProductStatusView from './WorkSheetProductStatusView'
import SingleWorkSheetInnerReadOnly from './SingleWorkSheetInnerReadOnly'
import { workSheetStatusInversed } from '@teinor/erp/types/company/order/sell/workSheet'
import { getConfigParam, getUserPermissions } from '../../../../../../../../utils/reducers/getters'
import { permissionCheck } from '../../../../../../../../baseComponents/PermissionChecker/function'
import { UserPermissions } from '../../../../../../../../baseComponents/CreateRoutering/types'
import { DefaultOrder, TableConfiguration } from '../../../../../../../../baseComponents/TwinTable/types'
import { OrderData } from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { addInventPositionToOrderData, buildOrderFieldsOrderLayout } from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/useOrderScreenLogic/functions'
import { ExtraParseOrderDataFnc } from '../../../../../../../../specificComponents/inputTableLayouts/order/useOrderScreenLogic/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../../../../utils/reducers/company/employees'
import { WorkSheetGroupStatusesPayload } from '../../../../../../../../utils/reducers/sells/workSheetGroupStatus'
import { LabelsPayload } from '../../../../../../../../utils/reducers/company/labels'
import useInputTableFilterData from '../../../../../../../../utils/hooks/orders/useInputTableFilterData'

interface SingleWorkSheetProps extends TableComponentProps { }

const SingleWorkSheet: React.FC<SingleWorkSheetProps> = ({ userPermissions }) => {
    const params = useParams<{ id: string }>()
    const inputTableURLs: InputTableURLsTable = {
        createData: ['', {}],
        getData: ['/api/app/order/sell/workSheet/getWorkSheetComplete', { id: params.id }],
        updateData: '/api/app/order/sell/workSheet/workSheetProduct/updateWorkSheetProduct',
        delete: '/api/app/order/sell/workSheet/workSheetProduct/deleteWorkSheetProduct',
        updateHeaderData: '',
        getSubProducts: ['', []],
        updateOrderLines: '/api/app/order/sell/workSheet/workSheetProduct/updateWorkSheetNLines',
        printTags: ''
    }
    
    const { tableConfiguration, setTableConfiguration, completeData, setCompleteData, getCompleteData, headerChanges } = useWorkSheetScreenLogic({ inputTableURLs, userPermissions })
    const { openStatusView, handleChangeView } = useSingleWorkSheetLogic({ getCompleteData })
    if (openStatusView) { 
        return <WorkSheetProductStatusView completeData={completeData} handleChangeView={handleChangeView} WorkSheetId={parseInt(params.id || '0')} />
    } else if (parseInt(completeData?.header?.status) === workSheetStatusInversed['finished'] || !permissionCheck(getUserPermissions('sell.workSheetProductStatus'), 'update') || completeData?.header?.active === false) {
        return <SingleWorkSheetInnerReadOnly {...{ completeData, setCompleteData, getCompleteData, inputTableURLs, userPermissions, tableConfiguration, setTableConfiguration, handleChangeView, headerChanges }} />
    }
    return <SingleWorkSheetInner {...{ completeData, setCompleteData, getCompleteData, inputTableURLs, userPermissions, tableConfiguration, setTableConfiguration, handleChangeView, headerChanges }} />
}

interface SingleWorkSheetLogicProps {
    getCompleteData: () => Promise<void>
}
const useSingleWorkSheetLogic = ({ getCompleteData }: SingleWorkSheetLogicProps) => {
    const [openStatusView, setOpenStatusView] = useState<boolean>(getConfigParam('defaultEnterWorksheetView') === 'product_status_view')

    const handleChangeView = useCallback(async () => {
        await getCompleteData()
        setOpenStatusView((old) => !old)
    }, [setOpenStatusView, getCompleteData])

    return { openStatusView, handleChangeView }
}

interface OrderScreenLogicProps {
    inputTableURLs: InputTableURLsTable
    extraParseFunctionData?: ExtraParseOrderDataFnc
    userPermissions: UserPermissions
    defaultOrder?: DefaultOrder
}

const useWorkSheetScreenLogic = ({ inputTableURLs }: OrderScreenLogicProps) => {
    const [completeData, setCompleteData] = useState<OrderData | null>(null)
    const [tableConfiguration, setTableConfiguration] = useState<TableConfiguration>({
        generalSearchbar: '',
        order: {},
        offset: 0,
        singleFieldsSearch: {},
        petitionNumber: 0
    })
    const { parseTableData } = useInputTableFilterData({ setCompleteData })
    const changeSingleField = useCallback((fieldName: string, value: string | string[]) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset: 0,
                singleFieldsSearch: {
                    ...copyOld.singleFieldsSearch,
                    [fieldName]: value
                }
            }
        })
    }, [setTableConfiguration])
    const myTableConfigurationStr = JSON.stringify(tableConfiguration)
    const getDataStr = JSON.stringify(inputTableURLs.getData)
    const getCompleteData = useCallback(async () => {
        const myTableConfigurationParsed = JSON.parse(myTableConfigurationStr)
        const getDataParsed = JSON.parse(getDataStr)
        const order = buildOrderFieldsOrderLayout(myTableConfigurationParsed.order)
        let result = await twinFetchPostJSON(getDataParsed[0], { ...getDataParsed[1], includeRelations: true, order })
        result = addInventPositionToOrderData(result)
        let processedTableLength = 0
        result.dictPosTableData = {}
        for (const key in result.tableData) {
            const element = result.tableData[key]
            if (element.processed) {
                processedTableLength++
            }
            const myParsedKey = parseInt(key)
            element.absoluteIndex = myParsedKey
            result.dictPosTableData[element.id] = myParsedKey
        }
        result.processedTableLength = processedTableLength
        return parseTableData((old) => old, result)
    }, [getDataStr, myTableConfigurationStr, parseTableData])

    useEffect(() => {
        getCompleteData()
    }, [getCompleteData])

    return { completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, headerChanges: { changeSingleField, changeOrder: () => { } } }
}

const workSheetGroupStatusesDispatch = {
    setWorkSheetGroupStatuses: (payload: WorkSheetGroupStatusesPayload) => ({ type: 'CHANGE_WORKSHEETGROUPSTATUSES', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setLabels: (payload: LabelsPayload) => ({ type: 'CHANGE_LABELS', payload }),
}

export type ReduxWorkSheetGroupStatus = ConnectedProps<typeof workSheetGroupStatusConnect>
const mapWorkSheetGroupStatusConnector = (state: AllReduxPayloads) => ({ workSheetGroupStatuses: state.workSheetGroupStatuses, employees: state.employees, labels: state.labels })
const workSheetGroupStatusConnect = connect(mapWorkSheetGroupStatusConnector, workSheetGroupStatusesDispatch)
const workSheetGroupStatusConnectLoading = withLoading(SingleWorkSheet, [{ fetchUrl: '/api/app/order/sell/workSheet/workSheetGroupStatus/getAllWorkSheetGroupStatusesComplete', propName: 'workSheetGroupStatuses', setFunctionName: 'setWorkSheetGroupStatuses' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/config/label/getAllLabels', propName: 'labels', setFunctionName: 'setLabels' }])
const SingleWorkSheetConnect = workSheetGroupStatusConnect(workSheetGroupStatusConnectLoading)

export default SingleWorkSheetConnect